// Shared, client-safe types for the model-comparison panels.
//
// These are imported by BOTH the server builder (`@/lib/product/build-model-panel`,
// which renders the default panel and serves on-demand panels via the route
// handler) AND the client component that renders fetched panels. Keep this module
// free of any server-only runtime imports so the client bundle can import the
// view-model types without dragging in the builder/SQL.

/** The same call signature as `ServerContext['t']` and `useTranslation().t`. */
export type TranslateFn = (key: string, fallback?: string, args?: Record<string, string | number>) => string

/** Specs beyond this index are hidden until "show all features" is toggled. */
export const FEATURES_LIMIT = 9

export interface PanelSpecMapping {
  id: string
  name: string
  value: string
  tooltipId: string
}

export interface PanelSpecGroup {
  id: string
  name: string
  mappings: PanelSpecMapping[]
}

export interface HighlightSpec {
  name: string
  value: string
  type: 'hardware' | 'software'
  genericName: string
  genericDescription: string
  proprietaryName: string | null
}

export interface UpgradeHighlight {
  name: string
  value: string
  /** The same feature's value on the tier being compared against; null when
   *  the lower tier lacks the feature entirely (an "Added" upgrade). */
  baseValue: string | null
  excludedModelIds: number[]
  type: 'hardware' | 'software'
  genericName: string
  genericDescription: string
  proprietaryName: string | null
  proprietaryDescription: string | null
  disclaimer: boolean
}

export interface LevelPriceInfo {
  price: number
  url: string | null
  sellerName: string | null
  sellerType: string | null
}

// Builder output stamps each mapping with a precomputed (content-hashed)
// tooltip id. This makes the panel renderable identically on the server
// (default panel) and the client (fetched panels), and lets the per-panel
// tooltip maps merge without id collisions.
export interface HighlightWithTooltip extends HighlightSpec {
  tooltipId: string
}
export interface UpgradeWithTooltip extends UpgradeHighlight {
  tooltipId: string
}

/** One model+level panel's fully-translated, tooltip-stamped view-model. This is
 *  what the route handler serializes and the client renders. */
export interface ModelPanelData {
  modelId: number
  levelId: number | undefined
  modelName: string
  modelShortName: string
  modelImage: string | null
  score: number | null
  levelPrice: LevelPriceInfo | null
  levelName: string | undefined
  /** For the panel's AffiliateAction tracking attributes. */
  releaseSlug: string
  specs: PanelSpecGroup[]
  uniqueToModel: HighlightWithTooltip[]
  upgradeHighlights: UpgradeWithTooltip[]
}
